// The operator's workspace inside a call or a Listen session: the prep, and
// beside or below it the existing Breeze panel as the Live tab with the prep
// chat as the second (two columns when `columns` is set, stacked otherwise).
// The panel is the same component with the same props a guest
// never sees change; only its header is dropped (the workspace has the tabs)
// and its Escape key is told nothing, so a stray Escape cannot close the dock.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import type { AssistantStore } from "../hooks/useAssistant";
import type { PanelStore } from "../hooks/usePanel";
import { prepGoals as fetchPrepGoals } from "../lib/prepApi";
import { liveFromCall } from "../lib/prepLive";
import { BreezePanel, type PrepGoals, type RecordingShown } from "./BreezePanel";
import { PrepWorkspace } from "./PrepWorkspace";

interface CallPrepWorkspaceProps {
  /** The prep this call is for; null opens the list of preps. */
  readonly slug: string | null;
  readonly panel: PanelStore;
  readonly assistant: AssistantStore;
  readonly selfId: string | null;
  readonly isHost: boolean;
  readonly sharingScreen: boolean;
  /** Whether the call is being recorded (see BreezePanel). */
  readonly recording?: RecordingShown;
  /** The docked desktop layout: prep and Breeze as full-height columns. */
  readonly columns?: boolean;
  /** The call screen owns which of the two columns is shut. */
  readonly breezeShut?: boolean;
  readonly onBreezeShutChange?: (shut: boolean) => void;
  readonly prepShut?: boolean;
  readonly onPrepShutChange?: (shut: boolean) => void;
}

const noop = (): void => {};

const goalsKey = (slug: string): string => `breeze:prepgoals:${slug}`;

/** The host's goals and opening question from the prep open in the call, once
 * per prep per device (row 257), so they show before the live assistant is on. */
function usePrepGoals(slug: string | null, isHost: boolean): PrepGoals | null {
  const [goals, setGoals] = useState<PrepGoals | null>(null);
  useEffect(() => {
    setGoals(null);
    if (!slug || !isHost) return;
    try {
      const kept = localStorage.getItem(goalsKey(slug));
      if (kept) {
        setGoals(JSON.parse(kept) as PrepGoals);
        return;
      }
    } catch {
      // Storage refused: ask the server.
    }
    const ctl = new AbortController();
    fetchPrepGoals(slug, ctl.signal)
      .then((g) => {
        if (!g.goal) return;
        setGoals(g);
        try {
          localStorage.setItem(goalsKey(slug), JSON.stringify(g));
        } catch {
          // Not kept; asked again next time.
        }
      })
      .catch(() => {});
    return () => ctl.abort();
  }, [slug, isHost]);
  return goals;
}

export function CallPrepWorkspace({
  slug,
  panel,
  assistant,
  selfId,
  isHost,
  sharingScreen,
  recording,
  columns = false,
  breezeShut,
  onBreezeShutChange,
  prepShut,
  onPrepShutChange,
}: CallPrepWorkspaceProps): JSX.Element {
  // The chat asks for the call's text when a message goes out, so it reads the
  // latest stores through a ref rather than the render it was built in.
  const latest = useRef({ shared: panel.shared, host: assistant.host });
  latest.current = { shared: panel.shared, host: assistant.host };
  const prepGoals = usePrepGoals(slug, isHost);
  const live = useCallback((): string => liveFromCall(latest.current.shared, latest.current.host), []);

  return (
    <PrepWorkspace
      slug={slug}
      columns={columns}
      breezeShut={breezeShut}
      onBreezeShutChange={onBreezeShutChange}
      prepShut={prepShut}
      onPrepShutChange={onPrepShutChange}
      host={isHost}
      live={live}
      edit={assistant.prepEdit}
      onDismissEdit={assistant.dismissPrepEdit}
      breezeLive={
        <BreezePanel panel={panel} assistant={assistant} selfId={selfId} isHost={isHost} sharingScreen={sharingScreen} recording={recording} layout="dock" onClose={noop} prepGoals={prepGoals} />
      }
    />
  );
}
